/**
 * 用户管理列表页面
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Button, Space, Input, Form, Select, Avatar, Skeleton,
  Modal, message, Tag, Popconfirm, Statistic, Row, Col, Switch, TreeSelect,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
  UserOutlined, LockOutlined, CheckCircleOutlined, StopOutlined,
  LoginOutlined, EyeOutlined, ApartmentOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import {
  getUserList, deleteUser, resetUserPassword, updateUserStatus,
  batchDeleteUsers, batchUpdateUserStatus, getUserStatistics,
  batchAddUsersToOrg,
} from '../../api/user';
import Permission from '@/modules/shared/components/common/Permission';
import {
  PERM_USERS_CREATE, PERM_USERS_UPDATE, PERM_USERS_DELETE,
} from '@/constants/permissions';
import { getOrganizationTree } from '../../api/organization';
import dayjs from 'dayjs';
import type { User, UserQueryParams, UserStatistics, OrganizationTreeNode } from '../../api/types';
import { UserStatus } from '../../api/types';
import UserForm from './UserForm';

const formatTime = (v: string | undefined | null) =>
  v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-';

const { confirm } = Modal;

// 将组织树节点转换为 TreeSelect 需要的格式
// API returns nodes with id/name fields; fallback to key/title/value if already transformed
const convertTreeData = (nodes: any[]): any[] =>
  nodes.map(node => ({
    title: node.title ?? node.name,
    value: String(node.value ?? node.id),
    key: String(node.key ?? node.id),
    children: node.children?.length ? convertTreeData(node.children) : [],
  }));

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string>('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [searchForm] = Form.useForm();

  // 批量加入组织
  const [batchOrgVisible, setBatchOrgVisible] = useState(false);
  const [batchOrgId, setBatchOrgId] = useState<string>();
  const [orgTreeData, setOrgTreeData] = useState<any[]>([]);

  // 获取用户列表
  const fetchUserList = useCallback(async () => {
    setLoading(true);
    try {
      const formValues = searchForm.getFieldsValue();
      const params: UserQueryParams = {
        ...formValues,
        current: pagination.current,
        pageSize: pagination.pageSize,
      };

      const res: any = await getUserList(params);
      if (res?.data) {
        const data = res.data?.data || res.data;
        setList(Array.isArray(data) ? data : (data?.list || []));
        setTotal(data?.total || data?.count || 0);
      }
    } catch (error) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchForm]);

  // 获取统计数据
  const fetchStatistics = async () => {
    setStatisticsLoading(true);
    try {
      const res: any = await getUserStatistics();
      if (res?.data) {
        setStatistics(res.data?.data || res.data);
      }
    } catch {
      // 统计接口可能不存在(404)，静默处理
    } finally {
      setStatisticsLoading(false);
    }
  };

  // 加载组织树（搜索 + 批量加入组织 共用）
  const fetchOrgTree = async () => {
    try {
      const res: any = await getOrganizationTree();
      const tree = res?.data?.tree || res?.data?.data || (Array.isArray(res?.data) ? res.data : []);
      setOrgTreeData(Array.isArray(tree) && tree.length ? convertTreeData(tree) : []);
    } catch {
      // 非关键数据，静默处理
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchOrgTree();
    fetchStatistics();
  }, []);

  useEffect(() => {
    fetchUserList();
  }, [pagination.current, pagination.pageSize]);

  // 处理表格变更
  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  // 搜索
  const handleSearch = () => {
    if (pagination.current === 1) {
      // 已在第 1 页时手动触发请求
      fetchUserList();
    } else {
      setPagination({ ...pagination, current: 1 });
    }
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    if (pagination.current === 1) {
      fetchUserList();
    } else {
      setPagination({ ...pagination, current: 1 });
    }
  };

  // 新增用户
  const handleAdd = () => {
    setCurrentUser(null);
    setFormModalVisible(true);
  };

  // 编辑用户
  const handleEdit = (record: User) => {
    setCurrentUser(record);
    setFormModalVisible(true);
  };

  // 删除用户
  const handleDelete = async (id: string) => {
    try {
      await deleteUser(id);
      message.success('删除成功');
      fetchUserList();
      fetchStatistics();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }

    confirm({
      title: '确定要删除所选用户吗?',
      icon: <ExclamationCircleOutlined />,
      content: `此操作将永久删除所选 ${selectedRowKeys.length} 个用户，是否继续？`,
      onOk: async () => {
        try {
          await batchDeleteUsers(selectedRowKeys.map(key => String(key)));
          message.success('批量删除成功');
          fetchUserList();
          fetchStatistics();
          setSelectedRowKeys([]);
        } catch (error) {
          message.error('批量删除失败');
        }
      },
    });
  };

  // 批量启用
  const handleBatchEnable = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }

    confirm({
      title: '确定要启用所选用户吗?',
      icon: <CheckCircleOutlined />,
      content: `此操作将启用所选 ${selectedRowKeys.length} 个用户`,
      onOk: async () => {
        try {
          await batchUpdateUserStatus(
            selectedRowKeys.map(key => String(key)),
            UserStatus.NORMAL,
          );
          message.success('批量启用成功');
          fetchUserList();
          fetchStatistics();
          setSelectedRowKeys([]);
        } catch (error) {
          message.error('批量启用失败');
        }
      },
    });
  };

  // 批量加入组织
  const handleBatchAddToOrg = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }
    if (orgTreeData.length === 0) {
      fetchOrgTree();
    }
    setBatchOrgVisible(true);
  };

  const handleConfirmBatchAddToOrg = async () => {
    if (!batchOrgId) {
      message.warning('请选择目标组织');
      return;
    }
    try {
      await batchAddUsersToOrg({
        user_ids: selectedRowKeys.map(key => String(key)),
        org_id: batchOrgId,
      });
      message.success('批量添加到组织成功');
      setBatchOrgVisible(false);
      setBatchOrgId(undefined);
      setSelectedRowKeys([]);
    } catch {
      message.error('批量添加到组织失败');
    }
  };

  // 批量禁用
  const handleBatchDisable = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }

    confirm({
      title: '确定要禁用所选用户吗?',
      icon: <StopOutlined />,
      content: `此操作将禁用所选 ${selectedRowKeys.length} 个用户`,
      onOk: async () => {
        try {
          await batchUpdateUserStatus(
            selectedRowKeys.map(key => String(key)),
            UserStatus.DISABLED,
          );
          message.success('批量禁用成功');
          fetchUserList();
          fetchStatistics();
          setSelectedRowKeys([]);
        } catch (error) {
          message.error('批量禁用失败');
        }
      },
    });
  };

  // 切换用户状态
  const handleStatusChange = async (checked: boolean, record: User) => {
    const newStatus = checked ? UserStatus.NORMAL : UserStatus.DISABLED;
    try {
      await updateUserStatus(record.id, newStatus);
      message.success(checked ? '启用成功' : '禁用成功');
      fetchUserList();
      fetchStatistics();
    } catch (error) {
      message.error('状态更新失败');
    }
  };

  // 打开重置密码弹窗
  const handleOpenResetPassword = (id: string) => {
    setResetPasswordUserId(id);
    setResetPasswordValue('');
    setResetPasswordModalVisible(true);
  };

  // 确认重置密码
  const handleConfirmResetPassword = async () => {
    if (!resetPasswordValue) {
      message.warning('请输入新密码');
      return;
    }
    if (resetPasswordValue.length < 6) {
      message.warning('密码长度不能少于6位');
      return;
    }

    setResetPasswordLoading(true);
    try {
      await resetUserPassword(resetPasswordUserId, resetPasswordValue);
      message.success('密码重置成功');
      setResetPasswordModalVisible(false);
      setResetPasswordValue('');
    } catch (error) {
      message.error('密码重置失败');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // 表单提交成功的回调
  const handleFormSuccess = () => {
    setFormModalVisible(false);
    fetchUserList();
    fetchStatistics();
  };

  // 性别显示
  const getGenderText = (gender: string) => {
    const map: Record<string, string> = {
      '0': '保密',
      '1': '男',
      '2': '女',
    };
    return map[gender] || '保密';
  };

  const columns: ColumnsType<User> = useMemo(() => [
    {
      title: '头像',
      dataIndex: 'avatar',
      width: 70,
      fixed: 'left',
      render: (_, record) => (
        <Avatar
          src={record.avatarUrl || record.avatar}
          icon={<UserOutlined />}
          size="small"
        />
      ),
    },
    {
      title: '用户名',
      dataIndex: 'username',
      width: 120,
      fixed: 'left',
    },
    {
      title: '真实姓名',
      dataIndex: 'realName',
      width: 100,
    },
    {
      title: '性别',
      dataIndex: 'gender',
      width: 70,
      render: (gender) => getGenderText(gender),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      width: 130,
      render: (phone) => phone || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 180,
      render: (email) => email || '-',
    },
    {
      title: '部门',
      dataIndex: 'org_name',
      width: 120,
      render: (orgName) => orgName || '-',
    },
    {
      title: '岗位',
      dataIndex: 'positionName',
      width: 120,
      render: (positionName) => positionName || '-',
    },
    {
      title: '角色',
      dataIndex: 'roles',
      width: 200,
      render: (roles: User['roles']) => {
        if (!roles || roles.length === 0) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return (
          <Space size={[0, 4]} wrap>
            {roles.map(role => (
              <Tag color="blue" key={role.id}>
                {role.name}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (status, record) => (
        <Switch
          checked={status === UserStatus.NORMAL}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          onChange={(checked) => handleStatusChange(checked, record)}
        />
      ),
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginTime',
      width: 160,
      render: (time) => formatTime(time),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (time) => formatTime(time),
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/tenant/system/users/${record.id}`)}
          >
            详情
          </Button>
          <Permission permission={PERM_USERS_UPDATE}>
            <Button
              type="link"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            >
              编辑
            </Button>
          </Permission>
          <Permission permission={PERM_USERS_UPDATE}>
            <Button
              type="link"
              icon={<LockOutlined />}
              size="small"
              onClick={() => handleOpenResetPassword(record.id)}
            >
              重置密码
            </Button>
          </Permission>
          <Permission permission={PERM_USERS_DELETE}>
            <Popconfirm
              title="确定要删除此用户吗?"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                删除
              </Button>
            </Popconfirm>
          </Permission>
        </Space>
      ),
    },
  ], [navigate]);

  return (
    <div className="user-list-container">
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Skeleton loading={statisticsLoading} active paragraph={false}>
              <Statistic
                title="总用户数"
                value={statistics?.totaluser ?? 0}
                prefix={<UserOutlined />}
              />
            </Skeleton>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Skeleton loading={statisticsLoading} active paragraph={false}>
              <Statistic
                title="正常用户"
                value={statistics?.normaluser ?? 0}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Skeleton>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Skeleton loading={statisticsLoading} active paragraph={false}>
              <Statistic
                title="禁用用户"
                value={statistics?.disableduser ?? 0}
                valueStyle={{ color: '#cf1322' }}
                prefix={<StopOutlined />}
              />
            </Skeleton>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Skeleton loading={statisticsLoading} active paragraph={false}>
              <Statistic
                title="今日登录"
                value={statistics?.todayLogins ?? 0}
                valueStyle={{ color: '#1890ff' }}
                prefix={<LoginOutlined />}
              />
            </Skeleton>
          </Card>
        </Col>
      </Row>

      <Card title="用户管理" variant="borderless">
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="username">
            <Input placeholder="用户名" allowClear style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="realName">
            <Input placeholder="真实姓名" allowClear style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="phone">
            <Input placeholder="手机号" allowClear style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="status">
            <Select
              placeholder="状态"
              style={{ width: 100 }}
              allowClear
            >
              <Select.Option value={UserStatus.NORMAL}>启用</Select.Option>
              <Select.Option value={UserStatus.DISABLED}>禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="deptId">
            <TreeSelect
              placeholder="部门"
              style={{ width: 200 }}
              treeData={orgTreeData}
              allowClear
              showSearch
              treeNodeFilterProp="title"
              treeDefaultExpandAll
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Permission permission={PERM_USERS_CREATE}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增用户
              </Button>
            </Permission>
            <Permission permission={PERM_USERS_DELETE}>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBatchDelete}
                disabled={!selectedRowKeys.length}
              >
                批量删除
              </Button>
            </Permission>
            <Permission permission={PERM_USERS_UPDATE}>
              <Button
                icon={<CheckCircleOutlined />}
                onClick={handleBatchEnable}
                disabled={!selectedRowKeys.length}
              >
                批量启用
              </Button>
            </Permission>
            <Permission permission={PERM_USERS_UPDATE}>
              <Button
                icon={<StopOutlined />}
                onClick={handleBatchDisable}
                disabled={!selectedRowKeys.length}
              >
                批量禁用
              </Button>
            </Permission>
            <Permission permission={PERM_USERS_UPDATE}>
              <Button
                icon={<ApartmentOutlined />}
                onClick={handleBatchAddToOrg}
                disabled={!selectedRowKeys.length}
              >
                加入组织
              </Button>
            </Permission>
          </Space>
        </div>

        {/* 表格 */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          onChange={handleTableChange}
          scroll={{ x: 1800 }}
        />
      </Card>

      {/* 用户表单对话框 */}
      <UserForm
        visible={formModalVisible}
        user={currentUser}
        onCancel={() => setFormModalVisible(false)}
        onSuccess={handleFormSuccess}
        orgTreeData={orgTreeData}
      />

      {/* 重置密码对话框 */}
      <Modal
        title="重置密码"
        open={resetPasswordModalVisible}
        onOk={handleConfirmResetPassword}
        onCancel={() => {
          setResetPasswordModalVisible(false);
          setResetPasswordValue('');
        }}
        confirmLoading={resetPasswordLoading}
        width={400}
        destroyOnHidden
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: '#999', marginBottom: 8 }}>请输入新密码，密码长度不少于6位</p>
          <Input.Password
            placeholder="请输入新密码"
            value={resetPasswordValue}
            onChange={(e) => setResetPasswordValue(e.target.value)}
            minLength={6}
            maxLength={32}
          />
        </div>
      </Modal>

      {/* 批量加入组织对话框 */}
      <Modal
        title={`批量加入组织（已选 ${selectedRowKeys.length} 人）`}
        open={batchOrgVisible}
        onOk={handleConfirmBatchAddToOrg}
        onCancel={() => { setBatchOrgVisible(false); setBatchOrgId(undefined); }}
        width={400}
        destroyOnHidden
      >
        <div style={{ marginBottom: 8, fontWeight: 500 }}>选择目标组织</div>
        <TreeSelect
          style={{ width: '100%' }}
          treeData={orgTreeData}
          value={batchOrgId}
          onChange={setBatchOrgId}
          placeholder="请选择组织"
          treeDefaultExpandAll
          showSearch
          treeNodeFilterProp="title"
        />
      </Modal>
    </div>
  );
};

export default UserList;
